import Big from 'big.js'
import { getProductByAddress } from 'indices-list'
import { atom } from 'jotai'
import { subgraphLinks } from 'products'
import { fetchGraphQLData } from 'services/query/fetchers/data-graph'
import { Address, zeroAddress } from 'viem'
import { QUERY_INDEX_ANATOMY } from '../keys'
import { DEFAULT_REFETCH_INTERVAL } from '../refetch-intervals'
import { IndexAnatomyRequestAtoms, getAnatomyQuery } from './model'
import { createAtomsWithQuery } from '../atomic-factory'
import { z } from 'zod'

export type EscrowStatus = {
  total: Big
  isAllowedAmount: boolean
}

export type SubgraphAnatomy = {
  chainIdSet: string[]
  currencyIdSets: string[][]
  config: RawConfig
}

export type AnatomyWithExtra = {
  chainIds: number[]
  productAddress: Address
  chainId: number
} & SubgraphAnatomy

type RawConfig = {
  AUMDilutionPerSecond: string
  depositCustomCallback: boolean
  depositFeeInBP: string
  id: string
  metadata: string
  redemptionCustomCallback: boolean
  redemptionFeeInBP: string
  useCustomAUMFee: boolean
}

export type RawSubgraphAnatomy = {
  indexes: {
    chainID: string
    anatomy: {
      chainIdSet: string[]
      currencyIdSets: {
        chainIndex: string
        sets: string[]
      }[]
    }
    assets: {
      chainID: string
      chainIndex: string
      assets: {
        asset: Address
        decimals: number
        symbol: string
        currencyID: string
      }[]
    }[]
    config: RawConfig
  }[]
}

export const defaultIndexAnatomy: AnatomyWithExtra = {
  chainIdSet: ['7'],
  currencyIdSets: [['255'], ['1'], ['1']],
  chainIds: [42161, 43114, 137],
  productAddress: zeroAddress,
  chainId: 42161,
  config: {} as RawConfig,
}

const AnatomyResponseScheme = z.object({
  chainID: z.string(),
  anatomy: z.object({
    chainIdSet: z.array(z.string()),
    currencyIdSets: z.array(
      z.object({
        chainIndex: z.string(),
        sets: z.array(z.string()),
      }),
    ),
  }),
  assets: z.array(
    z.object({
      chainID: z.string(),
      chainIndex: z.string(),
      assets: z.array(
        z.object({
          asset: z.string(),
          decimals: z.number(),
          symbol: z.string(),
          currencyID: z.string(),
        }),
      ),
    }),
  ),
  config: z.object({
    AUMDilutionPerSecond: z.string(),
    depositCustomCallback: z.boolean(),
    depositFeeInBP: z.string(),
    id: z.string(),
    metadata: z.string(),
    redemptionCustomCallback: z.boolean(),
    redemptionFeeInBP: z.string(),
    useCustomAUMFee: z.boolean(),
  }),
})

const getIndexAnatomyQuery = async <T>({
  queryKey: [key, address],
}: {
  queryKey: [string, Address]
}) => {
  if (address === zeroAddress) {
    return defaultIndexAnatomy as T
  } else {
    const url = subgraphLinks[getProductByAddress(address).symbol.toUpperCase()]

    const query = getAnatomyQuery(address)
    const res = await fetchGraphQLData<RawSubgraphAnatomy>(url, query)
    if (!res) {
      throw new Error(`Fails in getIndexAnatomyQuery function with a key ${key} (updating cache)`)
    }
    const response = await res.indexes[0]
    const result = AnatomyResponseScheme.parse(response)

    const chainIds = result.assets.map(({ chainID }) => +chainID)

    return {
      chainIdSet: result.anatomy?.chainIdSet ?? 1n,
      currencyIdSets: result.anatomy
        ? result.anatomy.currencyIdSets
            .sort((a, b) => (+a.chainIndex > +b.chainIndex ? 1 : -1))
            .map((el) => el.sets)
        : [['1']],
      chainId: +result.chainID,
      chainIds,
      productAddress: address,
      config: result.config,
    } as T
  }
}

export const indexV2AnatomyAtomFactory = () => {
  const indexAddressAtom = atom<Address>(zeroAddress)

  const mainProperties = createAtomsWithQuery<
    AnatomyWithExtra,
    Address,
    null,
    null,
    [string, Address]
  >(
    { first: indexAddressAtom },
    QUERY_INDEX_ANATOMY,
    getIndexAnatomyQuery<AnatomyWithExtra>,
    DEFAULT_REFETCH_INTERVAL,
    'getIndexAnatomyQuery',
  )

  return { indexAddress: indexAddressAtom, ...mainProperties } as IndexAnatomyRequestAtoms
}
